import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, ValidatorFn, AbstractControl } from "@angular/forms";
import { Validators } from "@angular/forms";
import { NotificationsService } from "@services/notifications.service";
import { SetupWizardService } from "@services/setup-wizard.service";
import { TranslationService } from "@services/translation.service";
import { Subject } from "rxjs";
import { take, takeUntil, debounceTime } from "rxjs/operators";

@Component({
  selector: "app-wizard-users",
  templateUrl: "./wizard-users.component.html",
  styleUrls: ["./wizard-users.component.scss", "../wizard-styles.scss"],
})
export class WizardUsersComponent implements OnInit, OnDestroy {
  public destroy$: Subject<boolean> = new Subject<boolean>();
  public input = "";
  public users = [];
  public roles = [];
  public previousUsernames = [];
  public previousEmails = [];
  public usersFormArray;
  public isProcessing;
  public isLoadingRoles = true;
  constructor(
    private fb: FormBuilder,
    private wizardService: SetupWizardService,
    private notifications: NotificationsService,
    private translationService: TranslationService
  ) {}

  ngOnInit() {
    this.wizardService.usersInput$
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.isProcessing = response.isProcessing;
        this.usersFormArray = response.input;
      });

    this.wizardService.getRoles$().subscribe((response) => {
      if (response?.listItems) {
        this.roles = response.listItems.map(({ data }) => ({
          preventEdit: data?.prevent_edit,
          name: data?.name || "",
          title: data?.title || "",
        }));
      }
    });
    this.wizardService.getUsers$().subscribe((response) => {
      if (response?.listItems) {
        response.listItems.forEach(({ data }) => {
          if (data.email) this.previousEmails.push(data.email);
          if (data.cognito_username)
            this.previousUsernames.push(data.cognito_username);
        });
      }
    });
    this.usersFormArray.valueChanges
      .pipe(debounceTime(100))
      .subscribe((value) => {
        const previousUsernames = this.previousUsernames.reduce(
          (acc, username) => {
            acc[username] = 1;
            return acc;
          },
          {}
        );
        const previousEmails = this.previousEmails.reduce((acc, email) => {
          acc[email] = 1;
          return acc;
        }, {});

        const { emails, usernames } = value.reduce(
          (acc, form) => {
            acc.emails[form.email] = acc.emails[form.email]
              ? acc.emails[form.email] + 1
              : 1;
            acc.usernames[form.username] = acc.usernames[form.username]
              ? acc.usernames[form.username] + 1
              : 1;

            return acc;
          },
          { emails: { ...previousEmails }, usernames: { ...previousUsernames } }
        );
        this.usersFormArray.controls.forEach((control) => {
          if (control.get("status")?.value === "Created") return;
          const emailControl = control.get("email");
          const usernameControl = control.get("username");
          const emailErrors = control.get("email").errors || {};
          const usernameErrors = control.get("username").errors || {};
          delete emailErrors.notUnique;
          delete usernameErrors.notUnique;
          if (emails[emailControl.value] > 1) {
            emailErrors.notUnique = true;
          }

          if (usernames[usernameControl.value] > 1) {
            usernameErrors.notUnique = true;
          }

          Object.keys(emailErrors).length
            ? emailControl.setErrors(emailErrors)
            : emailControl.setErrors(null);

          Object.keys(usernameErrors).length
            ? usernameControl.setErrors(usernameErrors)
            : usernameControl.setErrors(null);
        });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  processEmails() {
    const emails = this.filterForEmails();
    this.input = "";
    this.addUsersToTable(emails);
  }

  filterForEmails() {
    const emails = this.input.split(/,|\n/).map((email) => email.trim());
    return emails.filter((email) => !!email);
  }

  addUsersToTable(emails) {
    let defaultRole = this?.roles?.find((role) => role.preventEdit);
    if (!defaultRole && this?.roles?.length)
      defaultRole = this?.roles?.find((role) => role.name);
    if (!defaultRole)
      return this.notifications.popNotification(
        "",
        this.translationService.getTranslatedContent(
          "no_roles_available._please_try_again_shortly",
          "No Roles available. Please try again shortly"
        )
      );
    defaultRole = defaultRole?.name;
    this.users = emails.map((email) => {
      const username = email.substring(0, email.indexOf("@")) || email;

      const user = {
        email: [
          email,
          [Validators.required, Validators.email, this.noSpaceValidator()],
        ],
        username: [
          username,
          [
            Validators.required,
            Validators.pattern(/^[^@]+$/),
            this.noSpaceValidator(),
          ],
        ],
        role: defaultRole,
        status: "Standby",
        statusMessage: "",
      };
      const userGroup = this.fb.group(user);
      userGroup.get("status").disable();
      this.usersFormArray.push(userGroup);
    });
  }

  uniqueEmailValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      let counter = 0;
      const value = control.value;
      if (this.previousEmails.includes(value)) return { notUnique: true };
      const hasDuplicate = !!this.usersFormArray.value.find(({ email }) => {
        if (email === value) counter++;
        if (counter >= 2) return true;
      });
      if (hasDuplicate) return { notUnique: true };
    };
  }

  uniqueUsernameValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      let counter = 0;
      const value = control.value;
      if (this.previousUsernames.includes(value)) return { notUnique: true };
      const hasDuplicate = !!this.usersFormArray.value.find(({ username }) => {
        if (username === value) counter++;
        if (counter >= 2) return true;
      });
      if (hasDuplicate) return { notUnique: true };
    };
  }

  noSpaceValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const hasSpace = (control.value || "").includes(" ");
      const isValid = !hasSpace;
      return isValid ? null : { whitespace: true };
    };
  }

  deleteUser(index) {
    this.usersFormArray.removeAt(Number(index));
  }

  createUserAccounts() {
    let usersToAttemptToCreate = this.usersFormArray.controls.filter(
      (control) => {
        if (control.valid && control.get("status").value !== "Created") {
          control.disable();
          control.get("status").setValue("Processing");
          return true;
        }
      }
    );

    this.wizardService
      .createUserAccounts$(usersToAttemptToCreate)
      .pipe(take(1))
      .subscribe((responses) => {
        responses.forEach((response: any) => {
          if (response?.json_data?.email)
            this.previousEmails.push(response?.json_data?.email);
          if (response?.json_data?.cognito_username)
            this.previousUsernames.push(response?.json_data?.cognito_username);
        });
      });
  }
}
